import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, type ApiResponse } from "@/api/client";
import { Plus, Zap, Edit2, Trash2, Loader2, Users, CheckCircle2, Sparkles } from "lucide-react";
import {
  JOURNEY_SUBJECT_TYPE_LABELS,
  JOURNEY_TRIGGER_TYPE_LABELS,
  JOURNEY_SEND_CLASS_LABELS,
  type JourneySubjectType,
  type JourneyTrigger,
  type JourneyTriggerType,
  type JourneySendClass,
} from "@ckgo2/shared";
import toast from "react-hot-toast";
import clsx from "clsx";

interface JourneyListItem {
  id: string;
  name: string;
  description?: string | null;
  enabled: boolean;
  subjectType: JourneySubjectType;
  trigger: JourneyTrigger;
  sendClass: JourneySendClass;
  stepCount: number;
  enrolledCount: number;
  completedCount: number;
  lastError?: string | null;
  isSystem: boolean;
  updatedAt: string;
}

function triggerSummary(t: JourneyTrigger): string {
  const label = JOURNEY_TRIGGER_TYPE_LABELS[t.type as JourneyTriggerType] ?? t.type;
  switch (t.type) {
    case "status_change":
      return `${label} → ${t.to.join(", ")}`;
    case "event":
      return `${label}: ${t.eventType}`;
    case "time_offset":
      return `${label}: ${t.daysOffset >= 0 ? "+" : ""}${t.daysOffset}d from ${t.dateField}`;
    case "inactivity":
      return `${label}: ${t.days}d on ${t.sinceField}`;
    case "field_change":
      return `${label}: ${t.field}`;
    case "recurring":
      return `${label}: every ${t.everyDays ?? "?"}d`;
    default:
      return label;
  }
}

export default function JourneysPage() {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["journeys"],
    queryFn: () => api.get<ApiResponse<JourneyListItem[]>>("/api/admin/journeys"),
  });

  const toggleMutation = useMutation({
    mutationFn: (id: string) => api.patch(`/api/admin/journeys/${id}/toggle`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["journeys"] });
    },
    onError: () => toast.error("Failed to toggle journey"),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/api/admin/journeys/${id}`),
    onSuccess: () => {
      toast.success("Journey deleted");
      queryClient.invalidateQueries({ queryKey: ["journeys"] });
    },
    onError: () => toast.error("Failed to delete journey"),
  });

  const seedMutation = useMutation({
    mutationFn: () =>
      api.post<{ data: { created: number; updated: number } }>("/api/admin/journeys/seed"),
    onSuccess: (r) => {
      toast.success(`Seed catalog applied (${r.data.created} new, ${r.data.updated} updated)`);
      queryClient.invalidateQueries({ queryKey: ["journeys"] });
    },
    onError: () => toast.error("Failed to seed catalog (admin only)"),
  });

  const journeys = data?.data ?? [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Journeys</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Universal automations — multi-step, event &amp; schedule driven, across any subject.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => seedMutation.mutate()}
            disabled={seedMutation.isPending}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-800"
            title="Idempotently create the 8 starter journeys (disabled)"
          >
            <Sparkles className="h-4 w-4" /> Seed catalog
          </button>
          <Link
            to="/journeys/new"
            className="bg-primary-600 hover:bg-primary-700 inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium text-white"
          >
            <Plus className="h-4 w-4" /> New journey
          </Link>
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      ) : journeys.length === 0 ? (
        <div className="rounded-lg border border-dashed border-gray-300 py-20 text-center dark:border-gray-700">
          <Zap className="mx-auto h-10 w-10 text-gray-300" />
          <p className="mt-3 text-gray-500 dark:text-gray-400">No journeys yet.</p>
          <p className="text-sm text-gray-400">
            Create one, or seed the starter catalog from the API (POST /api/admin/journeys/seed).
          </p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">
                  Name
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">
                  Subject
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">
                  Trigger
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">
                  Send
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">
                  Stats
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">
                  Enabled
                </th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-900">
              {journeys.map((j) => (
                <tr key={j.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                  <td className="px-4 py-3">
                    <Link
                      to={`/journeys/${j.id}`}
                      className="hover:text-primary-600 font-medium text-gray-900 dark:text-white"
                    >
                      {j.name}
                    </Link>
                    <div className="flex items-center gap-2">
                      {j.isSystem && (
                        <span className="text-[10px] uppercase tracking-wide text-gray-400">
                          system
                        </span>
                      )}
                      <span className="text-xs text-gray-400">{j.stepCount} steps</span>
                    </div>
                    {j.lastError && (
                      <div className="max-w-xs truncate text-xs text-red-500" title={j.lastError}>
                        {j.lastError}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                    {JOURNEY_SUBJECT_TYPE_LABELS[j.subjectType]}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                    {triggerSummary(j.trigger)}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={clsx(
                        "rounded-full px-2 py-0.5 text-xs",
                        j.sendClass === "marketing"
                          ? "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300"
                          : "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300",
                      )}
                    >
                      {JOURNEY_SEND_CLASS_LABELS[j.sendClass]}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">
                    <span className="inline-flex items-center gap-1" title="Enrolled">
                      <Users className="h-3.5 w-3.5" /> {j.enrolledCount}
                    </span>
                    <span className="ml-3 inline-flex items-center gap-1" title="Completed">
                      <CheckCircle2 className="h-3.5 w-3.5" /> {j.completedCount}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => toggleMutation.mutate(j.id)}
                      className={clsx(
                        "relative inline-flex h-5 w-9 items-center rounded-full transition-colors",
                        j.enabled ? "bg-primary-600" : "bg-gray-300 dark:bg-gray-600",
                      )}
                      aria-label="Toggle enabled"
                    >
                      <span
                        className={clsx(
                          "inline-block h-4 w-4 transform rounded-full bg-white transition-transform",
                          j.enabled ? "translate-x-4" : "translate-x-0.5",
                        )}
                      />
                    </button>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Link
                        to={`/journeys/${j.id}/enrollments`}
                        className="rounded p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-700"
                        title="Enrollments"
                      >
                        <Users className="h-4 w-4" />
                      </Link>
                      <Link
                        to={`/journeys/${j.id}`}
                        className="rounded p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-700"
                        title="Edit"
                      >
                        <Edit2 className="h-4 w-4" />
                      </Link>
                      <button
                        onClick={() => {
                          if (
                            confirm(`Delete journey "${j.name}"? This removes its enrollments.`)
                          ) {
                            deleteMutation.mutate(j.id);
                          }
                        }}
                        className="rounded p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/30"
                        title="Delete"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
